import React, { useState, useEffect } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';

interface RegistrationSetupFormProps {
	onSuccess: (setupIntentId: string) => void;
	onError: (error: string) => void;
}

const RegistrationSetupForm = ({ onSuccess, onError }: RegistrationSetupFormProps) => {
	const { t } = useTranslation();
	const stripe = useStripe();
	const elements = useElements();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	// Check for setup intent return from Stripe
	useEffect(() => {
		if (!stripe) return;

		const clientSecret = new URLSearchParams(window.location.search).get('setup_intent_client_secret');
		if (clientSecret) {
			stripe.retrieveSetupIntent(clientSecret).then(({ setupIntent }) => {
				if (setupIntent) {					switch (setupIntent.status) {
						case 'succeeded':
							onSuccess(setupIntent.id);
							break;
						case 'processing':
							setErrorMessage(t('register.paymentMethod.paymentProcessing', 'Your payment is being processed.'));
							break;
						case 'requires_payment_method':
							const errorMsg = t('register.paymentMethod.paymentNotSuccessful', 'Your payment was not successful, please try again.');
							setErrorMessage(errorMsg);
							onError(errorMsg);
							break;
						default:
							const defaultError = t('register.paymentMethod.somethingWentWrong', 'Something went wrong.');
							setErrorMessage(defaultError);
							onError(defaultError);
							break;
					}
				}
			});
		}
	}, [stripe, onSuccess, onError]);

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		if (!stripe || !elements) {
			return;
		}

		setIsSubmitting(true);
		setErrorMessage(null);

		const currentHost = window.location.host;
		const protocol = window.location.protocol;
		const baseUrl = `${protocol}//${currentHost}`;

		const { error } = await stripe.confirmSetup({
			elements,
			confirmParams: {
				return_url: `${baseUrl}/register`,
			},
		});
		if (error) {
			const errorMsg = error.message || t('register.paymentMethod.errorOccurred', 'An error occurred');
			setErrorMessage(errorMsg);
			onError(errorMsg);
			setIsSubmitting(false);
		}
		// Note: Don't set isSubmitting(false) here if no error, 
		// as the user will be redirected to the return_url
	};
	return (		<div className="space-y-6">
			<form onSubmit={handleSubmit} className="space-y-6">
				<div className="p-4 border rounded-sm bg-blue-50 border-blue-200">
					<h4 className="font-medium text-blue-900 mb-2">{t('register.paymentMethod.setupTitle', 'Payment Method Setup')}</h4>
					<p className="text-sm text-blue-700">
						{t('register.paymentMethod.setupDescription', 'Enter your payment details below. This will be used for your subscription billing.')}
					</p>
				</div>
				
				<PaymentElement 
					options={{
						layout: "tabs"
					}}
				/>
				
				{errorMessage && (
					<div className="p-3 bg-red-50 border border-red-200 rounded-sm">
						<p className="text-red-600 text-sm">{errorMessage}</p>
					</div>
				)}
						<Button 
					type="submit"
					disabled={!stripe || !elements || isSubmitting}
					className="w-full bg-digger-blue hover:bg-digger-blue-light"
				>
					{isSubmitting ? t('register.paymentMethod.processing', 'Processing...') : t('register.paymentMethod.savePaymentMethod', 'Save Payment Method')}
				</Button>
			</form>
			
			<div className="text-xs text-gray-500 text-center">
				<p>{t('register.paymentMethod.secureInfo', '🔒 Your payment information is secure and encrypted')}</p>
			</div>
		</div>
	);
};

export default RegistrationSetupForm;
